import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { 
  Settings,
  Mail,
  Database,
  Key,
  Save,
  AlertTriangle,
  CheckCircle,
  Lock,
  Loader2,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Play,
  Star,
  Info,
  Send,
  RefreshCw,
  FileText,
  Package,
  Plus,
  Trash2,
  Upload,
  Image,
  Edit,
  X,
  ShieldCheck,
  ShieldOff,
  Copy,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
  const { changePassword } = useAuth();
  
  // Provider settings state
  const [providersData, setProvidersData] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState("mock");
  const [providerConfigs, setProviderConfigs] = useState({});
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [savingProvider, setSavingProvider] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState({});

  // SMTP settings state
  const [smtpSettings, setSmtpSettings] = useState({
    host: "",
    port: "465",
    encryption: "ssl",
    username: "",
    password: "",
    from_email: "",
    from_name: "",
    reply_to: ""
  });
  const [smtpStatus, setSmtpStatus] = useState({
    is_configured: false,
    last_test: null
  });
  const [loadingSmtp, setLoadingSmtp] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  
  // Email sending toggle state
  const [emailSendingEnabled, setEmailSendingEnabled] = useState(false);
  const [togglingEmailSending, setTogglingEmailSending] = useState(false);

  // Change password state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // MFA state
  const [mfaStatus, setMfaStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaSetupStep, setMfaSetupStep] = useState(null); // null, 'setup', 'confirm', 'backup'
  const [mfaSetupData, setMfaSetupData] = useState({ secret: '', qr_uri: '' });
  const [mfaConfirmCode, setMfaConfirmCode] = useState('');
  const [mfaDisableCode, setMfaDisableCode] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState([]);
  const [mfaPassword, setMfaPassword] = useState('');
  const [mfaProcessing, setMfaProcessing] = useState(false);
  const [showMfaDisableDialog, setShowMfaDisableDialog] = useState(false);

  // Admin Email state (for password reset)
  const [adminEmail, setAdminEmail] = useState("");
  const [savingAdminEmail, setSavingAdminEmail] = useState(false);
  const [loadingAdminEmail, setLoadingAdminEmail] = useState(true);

  // Prompt Templates state
  const [promptTemplates, setPromptTemplates] = useState({
    master_template: "",
    preview_wrapper: "",
    final_wrapper: "",
    prompt_templates_version: 0,
    is_default: true
  });
  const [loadingPrompts, setLoadingPrompts] = useState(true);
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [activePromptTab, setActivePromptTab] = useState("master");  // master, preview, final

  // Industry Packs state
  const [industryPacks, setIndustryPacks] = useState([]);
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [savingPack, setSavingPack] = useState(false);
  const [editingPack, setEditingPack] = useState(null);

  // Fetch provider settings
  useEffect(() => {
    fetchProviders();
    fetchSmtpSettings();
    fetchPromptTemplates();
    fetchIndustryPacks();
    fetchMfaStatus();
    fetchAdminEmail();
  }, []);

  const fetchAdminEmail = async () => {
    try {
      setLoadingAdminEmail(true);
      const response = await fetch(`${API_URL}/api/admin/settings/email`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setAdminEmail(data.email || "");
      }
    } catch (error) {
      console.error("Failed to fetch admin email:", error);
    } finally {
      setLoadingAdminEmail(false);
    }
  };

  const handleSaveAdminEmail = async () => {
    setSavingAdminEmail(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: adminEmail.trim() })
      });
      if (response.ok) {
        toast.success("Admin email updated");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update email");
      }
    } catch (error) {
      console.error("Failed to save admin email:", error);
      toast.error("Failed to update email");
    } finally {
      setSavingAdminEmail(false);
    }
  };

  const fetchProviders = async () => {
    try {
      setLoadingProviders(true);
      const response = await fetch(`${API_URL}/api/settings/providers`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setProvidersData(data);
        setSelectedProvider(data.selected_provider || "mock");
        
        // Initialize provider configs from current_config
        const configs = {};
        data.providers.forEach(p => {
          configs[p.name] = { ...p.current_config };
        });
        setProviderConfigs(configs);
      }
    } catch (error) {
      console.error("Failed to fetch providers:", error);
      toast.error("Failed to load provider settings");
    } finally {
      setLoadingProviders(false);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      setLoadingSmtp(true);
      const response = await fetch(`${API_URL}/api/settings/smtp`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setSmtpSettings({
          host: data.config?.host || "",
          port: String(data.config?.port || "465"),
          encryption: data.config?.encryption || "ssl",
          username: data.config?.username || "",
          password: "", // Never pre-fill password
          from_email: data.config?.from_email || "",
          from_name: data.config?.from_name || "",
          reply_to: data.config?.reply_to || ""
        });
        setSmtpStatus({
          is_configured: data.is_configured,
          last_test: data.last_test
        });
      }
      
      // Also fetch email sending enabled status
      const enabledResp = await fetch(`${API_URL}/api/admin/settings/email-sending`, {
        credentials: "include"
      });
      if (enabledResp.ok) {
        const enabledData = await enabledResp.json();
        setEmailSendingEnabled(enabledData.enabled);
      }
    } catch (error) {
      console.error("Failed to fetch SMTP settings:", error);
    } finally {
      setLoadingSmtp(false);
    }
  };

  const handleToggleEmailSending = async (enabled) => {
    setTogglingEmailSending(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/email-sending`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ enabled })
      });
      if (response.ok) {
        setEmailSendingEnabled(enabled);
        toast.success(`Email sending ${enabled ? 'enabled' : 'disabled'}`);
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update setting");
      }
    } catch (error) {
      console.error("Failed to toggle email sending:", error);
      toast.error("Failed to update setting");
    } finally {
      setTogglingEmailSending(false);
    }
  };

  const handleSelectProvider = async (providerName) => {
    try {
      setSavingProvider(true);
      const response = await fetch(`${API_URL}/api/settings/providers?provider_name=${providerName}`, {
        method: "PATCH",
        credentials: "include"
      });
      
      if (response.ok) {
        setSelectedProvider(providerName);
        toast.success(`Provider changed to ${providersData?.providers.find(p => p.name === providerName)?.display_name}`);
        fetchProviders(); // Refresh to get updated is_selected
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to update provider");
      }
    } catch (error) {
      toast.error("Failed to update provider");
    } finally {
      setSavingProvider(false);
    }
  };

  const handleUpdateProviderConfig = async (providerName) => {
    try {
      setSavingProvider(true);
      const config = providerConfigs[providerName] || {};
      
      // Only send non-empty values
      const payload = {};
      if (config.api_key) payload.api_key = config.api_key;
      if (config.base_url) payload.base_url = config.base_url;
      if (config.limit) payload.limit = parseInt(config.limit, 10);
      if (config.keyword) payload.keyword = config.keyword;
      if (config.search_radius) payload.search_radius = parseInt(config.search_radius, 10);
      
      const response = await fetch(`${API_URL}/api/settings/providers/${providerName}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success(`${providerName} configuration saved`);
        fetchProviders(); // Refresh to get updated status
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to save configuration");
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSavingProvider(false);
    }
  };

  const handleTestProvider = async (providerName) => {
    try {
      setTestingProvider(providerName);
      const response = await fetch(`${API_URL}/api/settings/providers/${providerName}/test`, {
        method: "POST",
        credentials: "include"
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success(result.message || "Connection successful");
      } else {
        toast.error(result.message || "Connection failed");
      }
      fetchProviders(); // Refresh to show test result
    } catch (error) {
      toast.error("Test failed");
    } finally {
      setTestingProvider(null);
    }
  };

  const handleSaveSmtp = async () => {
    try {
      setSavingSmtp(true);
      const payload = {
        host: smtpSettings.host,
        port: parseInt(smtpSettings.port, 10),
        encryption: smtpSettings.encryption,
        username: smtpSettings.username,
        from_email: smtpSettings.from_email,
        from_name: smtpSettings.from_name,
        reply_to: smtpSettings.reply_to || null
      };
      
      // Only include password if provided
      if (smtpSettings.password) {
        payload.password = smtpSettings.password;
      }
      
      const response = await fetch(`${API_URL}/api/settings/smtp`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("SMTP settings saved");
        setSmtpSettings(prev => ({ ...prev, password: "" })); // Clear password field
        fetchSmtpSettings(); // Refresh status
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to save SMTP settings");
      }
    } catch (error) {
      toast.error("Failed to save SMTP settings");
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!testEmailAddress) {
      toast.error("Enter a test email address");
      return;
    }
    
    try {
      setTestingSmtp(true);
      const response = await fetch(`${API_URL}/api/settings/smtp/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to_email: testEmailAddress }),
        credentials: "include"
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success(result.message || "Test email sent");
      } else if (result.status === "blocked_by_safety") {
        toast.warning(result.message || "Email blocked by safety guards");
      } else {
        toast.error(result.message || "Test failed");
      }
      fetchSmtpSettings(); // Refresh to show test result
    } catch (error) {
      toast.error("Test failed");
    } finally {
      setTestingSmtp(false);
    }
  };

  const validatePasswordForm = () => {
    if (!passwordForm.currentPassword) {
      setPasswordError("Current password is required");
      return false;
    }
    if (!passwordForm.newPassword) {
      setPasswordError("New password is required");
      return false;
    }
    if (passwordForm.newPassword.length < 12) {
      setPasswordError("New password must be at least 12 characters");
      return false;
    }
    if (!/[a-zA-Z]/.test(passwordForm.newPassword)) {
      setPasswordError("New password must contain at least one letter");
      return false;
    }
    if (!/\d/.test(passwordForm.newPassword)) {
      setPasswordError("New password must contain at least one number");
      return false;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match");
      return false;
    }
    return true;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError("");

    if (!validatePasswordForm()) {
      return;
    }

    setChangingPassword(true);

    const result = await changePassword(
      passwordForm.currentPassword,
      passwordForm.newPassword
    );

    setChangingPassword(false);

    if (result.success) {
      toast.success("Password changed successfully");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
    } else {
      setPasswordError(result.error);
      toast.error(result.error);
    }
  };

  // MFA handlers
  const fetchMfaStatus = async () => {
    try {
      setMfaLoading(true);
      const response = await fetch(`${API_URL}/api/auth/mfa/status`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setMfaStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch MFA status:", error);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaSetup = async () => {
    if (!mfaPassword) {
      toast.error("Please enter your password to enable MFA");
      return;
    }
    
    setMfaProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/mfa/setup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: mfaPassword })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMfaSetupData(data);
        setMfaSetupStep('confirm');
        setMfaPassword('');
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to initialize MFA setup");
      }
    } catch (error) {
      toast.error("Failed to setup MFA");
    } finally {
      setMfaProcessing(false);
    }
  };

  const handleMfaConfirm = async () => {
    if (mfaConfirmCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    
    setMfaProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/mfa/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: mfaConfirmCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMfaBackupCodes(data.backup_codes);
        setMfaSetupStep('backup');
        setMfaConfirmCode('');
        setMfaStatus({ enabled: true, backup_codes_remaining: data.backup_codes.length });
        toast.success("MFA enabled successfully!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Invalid code");
      }
    } catch (error) {
      toast.error("Failed to confirm MFA");
    } finally {
      setMfaProcessing(false);
    }
  };

  const handleMfaDisable = async () => {
    if (!mfaPassword || !mfaDisableCode) {
      toast.error("Please enter your password and authentication code");
      return;
    }
    
    setMfaProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/mfa/disable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password: mfaPassword, code: mfaDisableCode })
      });
      
      if (response.ok) {
        setMfaStatus({ enabled: false, backup_codes_remaining: 0 });
        setShowMfaDisableDialog(false);
        setMfaPassword('');
        setMfaDisableCode('');
        toast.success("MFA disabled successfully");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to disable MFA");
      }
    } catch (error) {
      toast.error("Failed to disable MFA");
    } finally {
      setMfaProcessing(false);
    }
  };

  const copyBackupCodes = () => {
    const codesText = mfaBackupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success("Backup codes copied to clipboard");
  };

  const downloadBackupCodes = () => {
    const codesText = `LeadForge Admin Backup Codes\n${'='.repeat(30)}\n\nSave these codes in a secure location.\nEach code can only be used once.\n\n${mfaBackupCodes.join('\n')}\n`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leadforge-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Backup codes downloaded");
  };

  const closeMfaSetup = () => {
    setMfaSetupStep(null);
    setMfaSetupData({ secret: '', qr_uri: '' });
    setMfaConfirmCode('');
    setMfaBackupCodes([]);
    setMfaPassword('');
  };

  // Prompt Templates handlers
  const fetchPromptTemplates = async () => {
    try {
      setLoadingPrompts(true);
      const response = await fetch(`${API_URL}/api/settings/prompts`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setPromptTemplates(data);
      }
    } catch (error) {
      console.error("Failed to fetch prompts:", error);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const handleSavePrompts = async () => {
    try {
      setSavingPrompts(true);
      const response = await fetch(`${API_URL}/api/settings/prompts`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          master_template: promptTemplates.master_template,
          preview_wrapper: promptTemplates.preview_wrapper,
          final_wrapper: promptTemplates.final_wrapper
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Prompt templates saved (v${data.prompt_templates_version})`);
        fetchPromptTemplates();
      } else {
        toast.error("Failed to save prompt templates");
      }
    } catch (error) {
      console.error("Failed to save prompts:", error);
      toast.error("Failed to save prompt templates");
    } finally {
      setSavingPrompts(false);
    }
  };

  // Industry Packs handlers
  const fetchIndustryPacks = async () => {
    try {
      setLoadingPacks(true);
      const response = await fetch(`${API_URL}/api/industry-packs`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setIndustryPacks(data);
      }
    } catch (error) {
      console.error("Failed to fetch industry packs:", error);
    } finally {
      setLoadingPacks(false);
    }
  };

  const handleSavePack = async () => {
    if (!editingPack) return;
    
    try {
      setSavingPack(true);
      const isNew = !industryPacks.find(p => p.industry_key === editingPack.industry_key);
      const method = isNew ? "POST" : "PATCH";
      const url = isNew 
        ? `${API_URL}/api/industry-packs`
        : `${API_URL}/api/industry-packs/${editingPack.industry_key}`;
      
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editingPack)
      });
      
      if (response.ok) {
        toast.success(isNew ? "Industry pack created" : "Industry pack updated");
        setPackDialogOpen(false);
        setEditingPack(null);
        fetchIndustryPacks();
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to save industry pack");
      }
    } catch (error) {
      console.error("Failed to save pack:", error);
      toast.error("Failed to save industry pack");
    } finally {
      setSavingPack(false);
    }
  };

  const handleDeletePack = async (industryKey) => {
    if (!confirm(`Delete the ${industryKey} industry pack?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/industry-packs/${industryKey}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        toast.success("Industry pack deleted");
        fetchIndustryPacks();
      } else {
        toast.error("Failed to delete industry pack");
      }
    } catch (error) {
      toast.error("Failed to delete industry pack");
    }
  };

  const handleUploadReference = async (industryKey, files) => {
    // Validate max 10 files per upload
    if (files.length > 10) {
      toast.error("Maximum 10 files allowed per upload");
      return;
    }
    
    // Validate file types
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Allowed: JPEG, PNG, WebP`);
        return;
      }
    }
    
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }
      
      const response = await fetch(`${API_URL}/api/industry-packs/${industryKey}/upload-reference`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Reference screenshots uploaded");
        
        // Update editingPack state immediately with new references from response
        setEditingPack(prev => prev ? ({
          ...prev,
          reference_screenshots: data.reference_screenshots
        }) : prev);
        
        // Also update the main list immediately (no stale state)
        setIndustryPacks(prev => prev.map(pack => 
          pack.industry_key === industryKey 
            ? { ...pack, reference_screenshots: data.reference_screenshots }
            : pack
        ));
      } else {
        const err = await response.json();
        toast.error(err.detail || "Failed to upload reference screenshots");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload reference screenshots");
    }
  };

  const handleDeleteReference = async (industryKey, refIdOrFilename) => {
    try {
      const response = await fetch(`${API_URL}/api/industry-packs/${industryKey}/reference/${refIdOrFilename}`, {
        method: "DELETE",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success("Reference screenshot deleted");
        
        // Update editingPack state immediately from response
        setEditingPack(prev => prev ? ({
          ...prev,
          reference_screenshots: data.reference_screenshots
        }) : prev);
        
        // Also update the main list immediately (no stale state)
        setIndustryPacks(prev => prev.map(pack => 
          pack.industry_key === industryKey 
            ? { ...pack, reference_screenshots: data.reference_screenshots }
            : pack
        ));
      } else {
        toast.error("Failed to delete reference screenshot");
      }
    } catch (error) {
      toast.error("Failed to delete reference screenshot");
    }
  };

  const openEditPack = (pack) => {
    setEditingPack({ ...pack });
    setPackDialogOpen(true);
  };

  const openNewPack = () => {
    setEditingPack({
      industry_key: "",
      display_name: "",
      default_services: [],
      default_service_areas: [],
      default_faqs: [],
      schema_defaults: {},
      image_queries: {},
      reference_screenshots: [],
      max_service_radius_miles: 25,
      disallow_city_invention: true,
      disallow_service_invention: true
    });
    setPackDialogOpen(true);
  };

  const getProviderStatusBadge = (provider) => {
    if (!provider.implemented) {
      return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">Coming Soon</span>;
    }
    if (provider.name === "mock") {
      return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">Testing</span>;
    }
    if (provider.is_configured && provider.last_test_success) {
      return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Ready</span>;
    }
    if (provider.is_configured) {
      return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">Not Tested</span>;
    }
    return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">Not Configured</span>;
  };

  const renderProviderConfigFields = (provider) => {
    const config = providerConfigs[provider.name] || {};
    const setConfig = (key, value) => {
      setProviderConfigs(prev => ({
        ...prev,
        [provider.name]: { ...prev[provider.name], [key]: value }
      }));
    };

    const requiredFields = provider.config_fields.filter(f => f.required);
    const optionalFields = provider.config_fields.filter(f => !f.required);

    return (
      <div className="space-y-4 mt-4">
        {/* Required fields */}
        {requiredFields.map(field => (
          <div key={field.key} className="form-group">
            <Label htmlFor={`${provider.name}-${field.key}`}>
              {field.label} <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${provider.name}-${field.key}`}
              type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
              value={config[field.key] || ""}
              onChange={(e) => setConfig(field.key, e.target.value)}
              placeholder={field.type === "password" ? "Enter API key" : (field.default || "")}
            />
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
          </div>
        ))}

        {/* Optional/Advanced fields (collapsible) */}
        {optionalFields.length > 0 && (
          <Collapsible 
            open={advancedOpen[provider.name]} 
            onOpenChange={(open) => setAdvancedOpen(prev => ({ ...prev, [provider.name]: open }))}
          >
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                Advanced Settings
                {advancedOpen[provider.name] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 mt-2">
              {optionalFields.map(field => (
                <div key={field.key} className="form-group">
                  <Label htmlFor={`${provider.name}-${field.key}`}>{field.label}</Label>
                  <Input
                    id={`${provider.name}-${field.key}`}
                    type={field.type === "number" ? "number" : "text"}
                    value={config[field.key] || ""}
                    onChange={(e) => setConfig(field.key, e.target.value)}
                    placeholder={String(field.default || "")}
                  />
                  {field.description && (
                    <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                  )}
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 pt-2">
          <Button 
            onClick={() => handleUpdateProviderConfig(provider.name)}
            disabled={savingProvider}
            size="sm"
          >
            {savingProvider ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
            Save
          </Button>
          {provider.name !== "mock" && (
            <Button
              variant="outline"
              onClick={() => handleTestProvider(provider.name)}
              disabled={testingProvider === provider.name}
              size="sm"
            >
              {testingProvider === provider.name ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <Play size={14} className="mr-2" />
              )}
              Test Connection
            </Button>
          )}
        </div>

        {/* Last test result */}
        {provider.last_test_at && (
          <div className={`text-xs p-2 rounded ${provider.last_test_success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            Last test: {provider.last_test_success ? '✓ Success' : '✗ Failed'} - {provider.last_test_message}
            <br />
            <span className="text-muted-foreground">{new Date(provider.last_test_at).toLocaleString()}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="settings-page">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Configure lead data providers, email, and security settings</p>
      </div>
      
      <div className="page-content">
        <div className="max-w-4xl space-y-6">
          
          {/* Lead Data Provider Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Database size={20} className="text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Lead Data Provider</CardTitle>
                  <CardDescription>Configure where leads are sourced from</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingProviders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Provider selector */}
                  <div className="form-group">
                    <Label>Active Provider</Label>
                    <Select value={selectedProvider} onValueChange={handleSelectProvider}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {providersData?.providers.map(provider => (
                          <SelectItem key={provider.name} value={provider.name} disabled={!provider.implemented && provider.name !== "mock"}>
                            <div className="flex items-center gap-2">
                              <span>{provider.display_name}</span>
                              {!provider.implemented && <span className="text-xs text-muted-foreground">(Coming Soon)</span>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Provider cards */}
                  <div className="space-y-4">
                    {providersData?.providers.map(provider => (
                      <div 
                        key={provider.name}
                        className={`border rounded-lg p-4 ${provider.is_selected ? 'border-primary bg-primary/5' : 'border-border'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{provider.display_name}</h4>
                              {getProviderStatusBadge(provider)}
                              {provider.is_selected && (
                                <span className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded-full">Active</span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{provider.description}</p>
                            
                            {/* Best for & Pros/Cons */}
                            <div className="mt-3 text-sm">
                              <p><strong>Best for:</strong> {provider.best_for}</p>
                              <div className="flex gap-4 mt-2">
                                <div>
                                  <p className="text-xs font-medium text-green-700">Pros:</p>
                                  <ul className="text-xs text-muted-foreground">
                                    {provider.pros.slice(0, 2).map((pro, i) => (
                                      <li key={i}>• {pro}</li>
                                    ))}
                                  </ul>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-red-700">Cons:</p>
                                  <ul className="text-xs text-muted-foreground">
                                    {provider.cons.slice(0, 2).map((con, i) => (
                                      <li key={i}>• {con}</li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Radius support indicator */}
                          {provider.supports_radius && (
                            <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                              <Info size={12} />
                              Supports Geo Radius
                            </div>
                          )}
                        </div>

                        {/* Configuration form (only for implemented providers or when selected) */}
                        {provider.implemented && provider.config_fields.length > 0 && (
                          <Collapsible defaultOpen={provider.is_selected}>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="mt-3">
                                <Settings size={14} className="mr-2" />
                                Configure
                                <ChevronDown size={14} className="ml-2" />
                              </Button>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              {renderProviderConfigFields(provider)}
                            </CollapsibleContent>
                          </Collapsible>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Important note */}
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">Important</p>
                      <p className="text-amber-700">
                        Campaigns will fail if a non-Mock provider is selected but not configured. 
                        Always test your provider connection before running campaigns.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SMTP Settings */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Mail size={20} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">Email (SMTP)</CardTitle>
                  <CardDescription>Configure outbound email delivery</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {smtpStatus.is_configured ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                      <CheckCircle size={12} /> Configured
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full flex items-center gap-1">
                      <AlertTriangle size={12} /> Not Configured
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingSmtp ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Email Sending Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-slate-50">
                    <div className="space-y-0.5">
                      <Label className="text-base font-medium">Email Sending</Label>
                      <p className="text-sm text-muted-foreground">
                        {emailSendingEnabled 
                          ? "Emails will be sent to recipients" 
                          : "Email sending is disabled (safety mode)"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {togglingEmailSending && <Loader2 size={16} className="animate-spin" />}
                      <Switch
                        checked={emailSendingEnabled}
                        onCheckedChange={handleToggleEmailSending}
                        disabled={togglingEmailSending}
                        data-testid="email-sending-toggle"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <Label>SMTP Host</Label>
                      <Input 
                        value={smtpSettings.host}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, host: e.target.value }))}
                        placeholder="mail.example.com"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Port</Label>
                      <Input 
                        value={smtpSettings.port}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, port: e.target.value }))}
                        placeholder="465"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label>Encryption</Label>
                    <Select value={smtpSettings.encryption} onValueChange={(v) => setSmtpSettings(prev => ({ ...prev, encryption: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ssl">SSL/TLS (Port 465)</SelectItem>
                        <SelectItem value="starttls">STARTTLS (Port 587)</SelectItem>
                        <SelectItem value="none">None (Testing Only)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <Label>Username</Label>
                      <Input 
                        value={smtpSettings.username}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="user@example.com"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Password</Label>
                      <Input 
                        type="password"
                        value={smtpSettings.password}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, password: e.target.value }))}
                        placeholder={smtpStatus.is_configured ? "••••••••" : "Enter password"}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Tip: If you change your mailbox password in SiteGround, update it here.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="form-group">
                      <Label>From Name</Label>
                      <Input 
                        value={smtpSettings.from_name}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_name: e.target.value }))}
                        placeholder="Your Name"
                      />
                    </div>
                    <div className="form-group">
                      <Label>From Email</Label>
                      <Input 
                        value={smtpSettings.from_email}
                        onChange={(e) => setSmtpSettings(prev => ({ ...prev, from_email: e.target.value }))}
                        placeholder="noreply@yourdomain.com"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <Label>Reply-To (Optional)</Label>
                    <Input 
                      value={smtpSettings.reply_to}
                      onChange={(e) => setSmtpSettings(prev => ({ ...prev, reply_to: e.target.value }))}
                      placeholder="Same as From Email if empty"
                    />
                  </div>

                  {/* Last test status */}
                  {smtpStatus.last_test && (
                    <div className={`text-sm p-3 rounded-lg ${
                      smtpStatus.last_test.status === 'success' ? 'bg-green-50 text-green-700' :
                      smtpStatus.last_test.status === 'blocked_by_safety' ? 'bg-yellow-50 text-yellow-700' :
                      'bg-red-50 text-red-700'
                    }`}>
                      <p className="font-medium">
                        Last test: {smtpStatus.last_test.status === 'success' ? '✓ Success' : 
                                   smtpStatus.last_test.status === 'blocked_by_safety' ? '⚠ Blocked by Safety' : '✗ Failed'}
                      </p>
                      {smtpStatus.last_test.error && <p className="text-xs mt-1">{smtpStatus.last_test.error}</p>}
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(smtpStatus.last_test.tested_at).toLocaleString()}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-4 pt-2">
                    <Button onClick={handleSaveSmtp} disabled={savingSmtp}>
                      {savingSmtp ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                      Save SMTP Settings
                    </Button>
                    
                    <div className="flex items-center gap-2">
                      <Input
                        type="email"
                        value={testEmailAddress}
                        onChange={(e) => setTestEmailAddress(e.target.value)}
                        placeholder="test@example.com"
                        className="w-48"
                      />
                      <Button variant="outline" onClick={handleTestSmtp} disabled={testingSmtp}>
                        {testingSmtp ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                        Test SMTP
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <Lock size={20} className="text-red-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Change Password</CardTitle>
                  <CardDescription>Update your admin password</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                {passwordError && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm flex items-center gap-2">
                    <AlertTriangle size={16} />
                    {passwordError}
                  </div>
                )}

                <div className="form-group">
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <div className="relative">
                    <Input 
                      id="currentPassword"
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                      placeholder="Enter current password"
                      disabled={changingPassword}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    >
                      {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input 
                      id="newPassword"
                      type={showPasswords.new ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      placeholder="Enter new password"
                      disabled={changingPassword}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    >
                      {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="form-hint">
                    Minimum 12 characters, at least 1 letter and 1 number
                  </p>
                </div>

                <div className="form-group">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input 
                      id="confirmPassword"
                      type={showPasswords.confirm ? "text" : "password"}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Confirm new password"
                      disabled={changingPassword}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    >
                      {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <Button type="submit" disabled={changingPassword} data-testid="change-password-btn">
                  {changingPassword ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Changing Password...
                    </>
                  ) : (
                    <>
                      <Lock size={16} className="mr-2" />
                      Change Password
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Admin Email (for password reset) */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Mail size={20} className="text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Admin Email</CardTitle>
                  <CardDescription>Email address for password reset and notifications</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingAdminEmail ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="animate-spin" size={20} />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="form-group">
                    <Label htmlFor="adminEmail">Email Address</Label>
                    <Input 
                      id="adminEmail"
                      type="email"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      placeholder="admin@example.com"
                      disabled={savingAdminEmail}
                      data-testid="admin-email-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This email will receive password reset links and important notifications.
                    </p>
                  </div>
                  <Button 
                    onClick={handleSaveAdminEmail} 
                    disabled={savingAdminEmail}
                    data-testid="save-admin-email-btn"
                  >
                    {savingAdminEmail ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        Save Email
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Two-Factor Authentication */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${mfaStatus.enabled ? 'bg-green-50' : 'bg-yellow-50'} rounded-lg flex items-center justify-center`}>
                  {mfaStatus.enabled ? (
                    <ShieldCheck size={20} className="text-green-600" />
                  ) : (
                    <ShieldOff size={20} className="text-yellow-600" />
                  )}
                </div>
                <div>
                  <CardTitle className="text-lg">Two-Factor Authentication</CardTitle>
                  <CardDescription>
                    {mfaStatus.enabled 
                      ? `MFA is enabled (${mfaStatus.backup_codes_remaining} backup codes remaining)`
                      : "Add an extra layer of security to your account"
                    }
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {mfaLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="animate-spin" size={24} />
                </div>
              ) : mfaSetupStep === 'setup' ? (
                // Step 1: Enter password to start setup
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Enter your password to enable two-factor authentication.
                  </p>
                  <div className="form-group">
                    <Label htmlFor="mfaSetupPassword">Password</Label>
                    <Input 
                      id="mfaSetupPassword"
                      type="password"
                      value={mfaPassword}
                      onChange={(e) => setMfaPassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={mfaProcessing}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleMfaSetup} disabled={mfaProcessing || !mfaPassword}>
                      {mfaProcessing ? <Loader2 className="mr-2 animate-spin" size={16} /> : null}
                      Continue
                    </Button>
                    <Button variant="outline" onClick={closeMfaSetup} disabled={mfaProcessing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : mfaSetupStep === 'confirm' ? (
                // Step 2: Scan QR code and enter code
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  
                  {/* QR Code */}
                  <div className="flex justify-center p-4 bg-white rounded-lg border">
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mfaSetupData.qr_uri)}`}
                      alt="MFA QR Code"
                      className="w-48 h-48"
                    />
                  </div>
                  
                  {/* Manual entry option */}
                  <Collapsible>
                    <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                      <ChevronDown size={16} />
                      Can't scan? Enter code manually
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <div className="p-3 bg-muted rounded-lg font-mono text-sm break-all">
                        {mfaSetupData.secret}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                  
                  {/* Verify code */}
                  <div className="form-group">
                    <Label htmlFor="mfaConfirmCode">Enter code from authenticator</Label>
                    <Input 
                      id="mfaConfirmCode"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={mfaConfirmCode}
                      onChange={(e) => setMfaConfirmCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="text-center text-xl tracking-widest font-mono max-w-40"
                      disabled={mfaProcessing}
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <Button onClick={handleMfaConfirm} disabled={mfaProcessing || mfaConfirmCode.length !== 6}>
                      {mfaProcessing ? <Loader2 className="mr-2 animate-spin" size={16} /> : null}
                      Verify & Enable
                    </Button>
                    <Button variant="outline" onClick={closeMfaSetup} disabled={mfaProcessing}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : mfaSetupStep === 'backup' ? (
                // Step 3: Show backup codes
                <div className="space-y-4">
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-start gap-2">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                      <strong>Save these backup codes!</strong>
                      <p className="mt-1">These codes can be used to access your account if you lose your authenticator. Each code can only be used once.</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
                    {mfaBackupCodes.map((code, i) => (
                      <div key={i} className="p-2 bg-background rounded border text-center">
                        {code}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={copyBackupCodes}>
                      <Copy size={16} className="mr-2" />
                      Copy
                    </Button>
                    <Button variant="outline" onClick={downloadBackupCodes}>
                      <Download size={16} className="mr-2" />
                      Download
                    </Button>
                    <Button onClick={closeMfaSetup} className="ml-auto">
                      Done
                    </Button>
                  </div>
                </div>
              ) : mfaStatus.enabled ? (
                // MFA is enabled - show status and disable option
                <div className="space-y-4">
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm flex items-center gap-2">
                    <ShieldCheck size={16} />
                    Two-factor authentication is enabled
                  </div>
                  
                  {mfaStatus.backup_codes_remaining <= 2 && (
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-center gap-2">
                      <AlertTriangle size={16} />
                      Only {mfaStatus.backup_codes_remaining} backup codes remaining. Consider regenerating them.
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setShowMfaDisableDialog(true)}
                  >
                    <ShieldOff size={16} className="mr-2" />
                    Disable MFA
                  </Button>
                  
                  {/* Disable MFA Dialog */}
                  {showMfaDisableDialog && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <Card className="w-full max-w-md m-4">
                        <CardHeader>
                          <CardTitle>Disable Two-Factor Authentication</CardTitle>
                          <CardDescription>
                            Enter your password and current MFA code to disable two-factor authentication.
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="form-group">
                            <Label>Password</Label>
                            <Input 
                              type="password"
                              value={mfaPassword}
                              onChange={(e) => setMfaPassword(e.target.value)}
                              placeholder="Enter your password"
                              disabled={mfaProcessing}
                            />
                          </div>
                          <div className="form-group">
                            <Label>Authentication Code</Label>
                            <Input 
                              type="text"
                              inputMode="numeric"
                              maxLength={8}
                              value={mfaDisableCode}
                              onChange={(e) => setMfaDisableCode(e.target.value)}
                              placeholder="000000 or backup code"
                              disabled={mfaProcessing}
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setShowMfaDisableDialog(false);
                                setMfaPassword('');
                                setMfaDisableCode('');
                              }}
                              disabled={mfaProcessing}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="destructive"
                              onClick={handleMfaDisable}
                              disabled={mfaProcessing || !mfaPassword || !mfaDisableCode}
                            >
                              {mfaProcessing ? <Loader2 className="mr-2 animate-spin" size={16} /> : null}
                              Disable MFA
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              ) : (
                // MFA is not enabled - show enable button
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Two-factor authentication adds an extra layer of security by requiring a code from your phone in addition to your password.
                  </p>
                  <Button onClick={() => setMfaSetupStep('setup')}>
                    <ShieldCheck size={16} className="mr-2" />
                    Enable Two-Factor Authentication
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* API Keys Status */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <Key size={20} className="text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Integration Status</CardTitle>
                  <CardDescription>Current API configuration summary</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">OpenAI GPT-5.2</p>
                    <p className="text-xs text-muted-foreground">Website content generation</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-500" />
                    <span className="text-sm text-green-600">Configured</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">Lead Data Provider</p>
                    <p className="text-xs text-muted-foreground">
                      {providersData?.providers.find(p => p.is_selected)?.display_name || "Mock"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedProvider === "mock" ? (
                      <>
                        <AlertTriangle size={16} className="text-yellow-500" />
                        <span className="text-sm text-yellow-600">Test Mode</span>
                      </>
                    ) : providersData?.providers.find(p => p.is_selected)?.is_configured ? (
                      <>
                        <CheckCircle size={16} className="text-green-500" />
                        <span className="text-sm text-green-600">Ready</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={16} className="text-red-500" />
                        <span className="text-sm text-red-600">Not Configured</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div>
                    <p className="font-medium text-sm">SMTP Email</p>
                    <p className="text-xs text-muted-foreground">Outreach email delivery</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {smtpStatus.is_configured ? (
                      <>
                        <CheckCircle size={16} className="text-green-500" />
                        <span className="text-sm text-green-600">Configured</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={16} className="text-yellow-500" />
                        <span className="text-sm text-yellow-600">Using ENV Fallback</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prompt Templates */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                  <FileText size={20} className="text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Prompt Templates</CardTitle>
                  <CardDescription>Master Prompt system for preview and full site generation</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPrompts ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Info size={14} />
                    <span>Version: {promptTemplates.prompt_templates_version}</span>
                    {promptTemplates.is_default && (
                      <Badge className="bg-green-100 text-green-700">Master Prompt V1 Active</Badge>
                    )}
                  </div>
                  
                  {/* Tab navigation */}
                  <div className="flex gap-1 p-1 bg-muted rounded-lg">
                    <button
                      onClick={() => setActivePromptTab("master")}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activePromptTab === "master" 
                          ? "bg-white shadow text-foreground" 
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Master Template
                    </button>
                    <button
                      onClick={() => setActivePromptTab("preview")}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activePromptTab === "preview" 
                          ? "bg-white shadow text-foreground" 
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Preview Wrapper
                    </button>
                    <button
                      onClick={() => setActivePromptTab("final")}
                      className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activePromptTab === "final" 
                          ? "bg-white shadow text-foreground" 
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Final Wrapper
                    </button>
                  </div>
                  
                  {/* Master Template */}
                  {activePromptTab === "master" && (
                    <div className="form-group">
                      <Label>Master Site Template (V1)</Label>
                      <Textarea
                        value={promptTemplates.master_template}
                        onChange={(e) => setPromptTemplates(prev => ({
                          ...prev,
                          master_template: e.target.value
                        }))}
                        placeholder="The core Master Prompt template with placeholders..."
                        className="min-h-[400px] font-mono text-xs"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Core template used by both PREVIEW and FINAL modes. Contains {"{PLACEHOLDER}"} variables.
                      </p>
                    </div>
                  )}
                  
                  {/* Preview Wrapper */}
                  {activePromptTab === "preview" && (
                    <div className="form-group">
                      <Label>Preview Wrapper (BUILD_MODE=PREVIEW)</Label>
                      <Textarea
                        value={promptTemplates.preview_wrapper}
                        onChange={(e) => setPromptTemplates(prev => ({
                          ...prev,
                          preview_wrapper: e.target.value
                        }))}
                        placeholder="Wrapper that sets BUILD_MODE=PREVIEW and enforces preview-only rules..."
                        className="min-h-[400px] font-mono text-xs"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enforces: Single homepage, mock nav, no schema, no branding, neutral defaults.
                      </p>
                    </div>
                  )}
                  
                  {/* Final Wrapper */}
                  {activePromptTab === "final" && (
                    <div className="form-group">
                      <Label>Final Wrapper (BUILD_MODE=FINAL)</Label>
                      <Textarea
                        value={promptTemplates.final_wrapper}
                        onChange={(e) => setPromptTemplates(prev => ({
                          ...prev,
                          final_wrapper: e.target.value
                        }))}
                        placeholder="Wrapper that sets BUILD_MODE=FINAL and enforces full build rules..."
                        className="min-h-[400px] font-mono text-xs"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enforces: Full multi-page site, dropdown menus, schema, branding, intake data.
                      </p>
                    </div>
                  )}
                  
                  <Button onClick={handleSavePrompts} disabled={savingPrompts}>
                    {savingPrompts ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save Prompt Templates
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Industry Packs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <Package size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Industry Packs</CardTitle>
                    <CardDescription>Predefined services, FAQs, and settings per industry</CardDescription>
                  </div>
                </div>
                <Button onClick={openNewPack} variant="outline" size="sm">
                  <Plus className="mr-2 h-4 w-4" />
                  New Pack
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingPacks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-3">
                  {industryPacks.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No industry packs configured. Create one to get started.
                    </p>
                  ) : (
                    industryPacks.map(pack => (
                      <div 
                        key={pack.industry_key}
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{pack.display_name}</span>
                            <Badge variant="outline" className="text-xs">
                              {pack.industry_key}
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {pack.default_services?.length || 0} services • 
                            {pack.default_faqs?.length || 0} FAQs • 
                            {pack.reference_screenshots?.length || 0} references • 
                            {pack.max_service_radius_miles}mi radius
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditPack(pack)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeletePack(pack.industry_key)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Industry Pack Edit Dialog */}
      <Dialog open={packDialogOpen} onOpenChange={setPackDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPack && industryPacks.find(p => p.industry_key === editingPack.industry_key) 
                ? `Edit: ${editingPack.display_name}` 
                : "New Industry Pack"}
            </DialogTitle>
          </DialogHeader>
          
          {editingPack && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Industry Key <span className="text-destructive">*</span></Label>
                  <Input
                    value={editingPack.industry_key}
                    onChange={(e) => setEditingPack(prev => ({
                      ...prev,
                      industry_key: e.target.value.toLowerCase().replace(/\s+/g, "_")
                    }))}
                    placeholder="e.g., hvac, plumbing"
                    disabled={industryPacks.find(p => p.industry_key === editingPack.industry_key)}
                  />
                </div>
                <div className="form-group">
                  <Label>Display Name <span className="text-destructive">*</span></Label>
                  <Input
                    value={editingPack.display_name}
                    onChange={(e) => setEditingPack(prev => ({
                      ...prev,
                      display_name: e.target.value
                    }))}
                    placeholder="e.g., HVAC, Plumbing"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <Label>Default Services (one per line)</Label>
                <Textarea
                  value={(editingPack.default_services || []).join("\n")}
                  onChange={(e) => setEditingPack(prev => ({
                    ...prev,
                    default_services: e.target.value.split("\n").filter(s => s.trim())
                  }))}
                  placeholder="AC Repair&#10;AC Installation&#10;Heating Repair..."
                  className="min-h-[120px]"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label>Max Service Radius (miles)</Label>
                  <Input
                    type="number"
                    value={editingPack.max_service_radius_miles}
                    onChange={(e) => setEditingPack(prev => ({
                      ...prev,
                      max_service_radius_miles: parseInt(e.target.value) || 25
                    }))}
                  />
                </div>
                <div className="form-group">
                  <Label>Price Range</Label>
                  <Select
                    value={editingPack.schema_defaults?.priceRange || "$$"}
                    onValueChange={(val) => setEditingPack(prev => ({
                      ...prev,
                      schema_defaults: { ...prev.schema_defaults, priceRange: val }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="$">$ - Budget</SelectItem>
                      <SelectItem value="$$">$$ - Moderate</SelectItem>
                      <SelectItem value="$$$">$$$ - Premium</SelectItem>
                      <SelectItem value="$$$$">$$$$ - Luxury</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingPack.disallow_city_invention}
                    onCheckedChange={(checked) => setEditingPack(prev => ({
                      ...prev,
                      disallow_city_invention: checked
                    }))}
                  />
                  <Label className="font-normal">Disallow City Invention</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={editingPack.disallow_service_invention}
                    onCheckedChange={(checked) => setEditingPack(prev => ({
                      ...prev,
                      disallow_service_invention: checked
                    }))}
                  />
                  <Label className="font-normal">Disallow Service Invention</Label>
                </div>
              </div>
              
              {/* Reference Screenshots section - only for existing packs */}
              {industryPacks.find(p => p.industry_key === editingPack.industry_key) && (
                <div className="form-group">
                  <Label>Reference Screenshots ({(editingPack.reference_screenshots || []).length}/10)</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {(editingPack.reference_screenshots || []).map((ref, idx) => {
                      // Support both object format {id, path, filename} and legacy string format
                      const imagePath = typeof ref === 'object' ? ref.path : ref;
                      const refId = typeof ref === 'object' ? (ref.id || ref.filename) : ref.split("/").pop();
                      return (
                        <div key={refId || idx} className="relative group aspect-square">
                          <img 
                            src={`${API_URL}${imagePath}`} 
                            alt={`Reference ${idx + 1}`}
                            className="w-full h-full object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteReference(editingPack.industry_key, refId)}
                            className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-reference-${idx}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      );
                    })}
                    {/* Upload button - only show if under limit */}
                    {(editingPack.reference_screenshots || []).length < 10 && (
                      <label 
                        className="aspect-square border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors"
                        data-testid="upload-reference-btn"
                      >
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          multiple
                          className="hidden"
                          data-testid="upload-reference-input"
                          onChange={(e) => {
                            if (e.target.files?.length > 0) {
                              const currentCount = (editingPack.reference_screenshots || []).length;
                              const selectedCount = e.target.files.length;
                              if (currentCount + selectedCount > 10) {
                                toast.error(`Cannot upload ${selectedCount} files. Only ${10 - currentCount} slots remaining (max 10 total).`);
                                e.target.value = ''; // Reset input
                                return;
                              }
                              if (selectedCount > 10) {
                                toast.error("Maximum 10 files allowed per upload");
                                e.target.value = '';
                                return;
                              }
                              handleUploadReference(editingPack.industry_key, Array.from(e.target.files));
                              e.target.value = ''; // Reset for next upload
                            }
                          }}
                        />
                        <Upload size={20} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground mt-1">Add</span>
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload up to 10 reference screenshots (JPEG, PNG, WebP). These guide website quality expectations.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePack} disabled={savingPack}>
              {savingPack ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Pack"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
